import { useEffect, useRef, useState, useCallback } from 'react';
import './ForecastSimulation.css';

/* ── Streaming data ── */
const PUBLIC_POOL = [
  'Reuters: Fed signals rate pause through Q3 2026',
  'Bloomberg: EU carbon credits hit record €98/ton',
  'AP: US-China trade talks resume in Geneva',
  'WSJ: TSMC expands Arizona fab capacity 40%',
  'FT: UK gilt yields climb to 14-year high',
  'Polymarket: 72% on Trump tariff reversal',
  'GDELT: South China Sea tension index +18.3',
  'BLS: Non-farm payrolls +187K (est. +200K)',
  'API: Crude inventories down 3.1M barrels',
  'Reuters: ECB holds rates, signals September cut',
  'Bloomberg: NVIDIA surpasses $4T market cap',
  'IMF: Global growth forecast revised to 2.9%',
  'AP: India bans rice exports amid monsoon delay',
  'WSJ: Boeing 737 MAX cleared for China delivery',
  'Reuters: Japan intervenes as yen hits ¥162',
  'Bloomberg: US CPI print 3.2% YoY (est 3.1%)',
  'OPEC+: Extends production cuts through Q4',
  'Metaculus: AI benchmark median at 78.4%',
  'Reuters: Samsung wins $6.4B CHIPS Act grant',
  'Bloomberg: VIX spikes to 28 on earnings miss',
];

const PRIVATE_POOL = [
  'Satellite: Strait of Hormuz traffic -12%',
  'Proprietary model: VIX regime shift detected',
  'Dr. Chen (ex-SEC): Enforcement wave incoming',
  'Field report: Foxconn Zhengzhou output -22%',
  'Insider model: Yield curve inversion signal',
  'Satellite: Chinese port activity surge +31%',
  'Dr. Okafor: WHO pandemic preparedness gaps',
  'Field report: Rare earth mine closures Ganzhou',
  'Proprietary model: Credit spread widening',
  'Expert network: ASML export controls tightening',
  'Satellite: Arctic shipping lane opening early',
  'Dr. Petrov: Russian grain harvest down 15%',
];

/* ── Agents — fewer, bigger, with step configs ── */
const AGENTS = [
  {
    id: 0, name: 'Geopolitical SME', sme: true,
    cy: 4, targets: [0, 1],
    steps: [
      { action: 'researching', tool: 'Brave Search' },
      { action: 'cross-referencing', tool: 'GDELT Monitor' },
      { action: 'estimating', tool: 'Internal Models' },
    ],
  },
  {
    id: 1, name: 'Macro Analyst', sme: false,
    cy: 22, targets: [0, 1],
    steps: [
      { action: 'pulling data', tool: 'FRED API' },
      { action: 'modeling', tool: 'BLS Database' },
      { action: 'forecasting', tool: 'Yield Curve Models' },
    ],
  },
  {
    id: 2, name: 'Market Analyst', sme: false,
    cy: 40, targets: [1, 2],
    steps: [
      { action: 'scanning', tool: 'Bloomberg Feed' },
      { action: 'pricing', tool: 'Options Models' },
    ],
  },
  {
    id: 3, name: 'Energy SME', sme: true,
    cy: 56, targets: [2],
    steps: [
      { action: 'analyzing', tool: 'EIA Database' },
      { action: 'interpreting', tool: 'Satellite Imagery' },
      { action: 'estimating', tool: 'Supply Models' },
    ],
  },
  {
    id: 4, name: 'Synthesis Agent', sme: false,
    cy: 74, targets: [0, 1, 2],
    steps: [
      { action: 'aggregating', tool: 'Agent Outputs' },
      { action: 'bias auditing', tool: 'Bias Detection' },
      { action: 'calibrating', tool: 'Ensemble Model' },
      { action: 'finalizing', tool: 'Confidence Scoring' },
    ],
  },
];

/* ── Market graph: 3-layer inverted pyramid ──
   L0: 3 common factors  (agents feed these)
   L1: 5 sub-factors
   L2: 8 terminal forecasts (ALL are outputs)
   Many L2 nodes share L1/L0 ancestors = decomposition + aggregation
*/
const MARKET_NODES = [
  // L0 — common factors (x=48)
  { id: 0, x: 48, y: 18, layer: 0 },
  { id: 1, x: 48, y: 50, layer: 0 },
  { id: 2, x: 48, y: 82, layer: 0 },
  // L1 — sub-factors (x=66)
  { id: 3, x: 66, y: 8,  layer: 1 },
  { id: 4, x: 66, y: 26, layer: 1 },
  { id: 5, x: 66, y: 44, layer: 1 },
  { id: 6, x: 66, y: 66, layer: 1 },
  { id: 7, x: 66, y: 88, layer: 1 },
  // L2 — terminal forecasts (x=92)
  { id: 8,  x: 92, y: 4,  layer: 2 },
  { id: 9,  x: 92, y: 16, layer: 2 },
  { id: 10, x: 92, y: 28, layer: 2 },
  { id: 11, x: 92, y: 40, layer: 2 },
  { id: 12, x: 92, y: 52, layer: 2 },
  { id: 13, x: 92, y: 64, layer: 2 },
  { id: 14, x: 92, y: 78, layer: 2 },
  { id: 15, x: 92, y: 92, layer: 2 },
];

// Sparse: factors shared across multiple forecasts
const CHILDREN = {
  // L0 → L1 (each factor feeds 2-3 sub-factors)
  0: [3, 4],
  1: [4, 5, 6],
  2: [6, 7],
  // L1 → L2 (each sub-factor feeds 1-3 terminal forecasts)
  3: [8, 9],
  4: [9, 10],       // Sub 4 shares forecast 9 with Sub 3
  5: [11, 12],
  6: [12, 13, 14],  // Sub 6 shares forecast 12 with Sub 5
  7: [14, 15],      // Sub 7 shares forecast 14 with Sub 6
};

const MARKET_EDGES = [];
Object.entries(CHILDREN).forEach(([from, kids]) => {
  kids.forEach((to) => MARKET_EDGES.push([Number(from), to]));
});

const NODE_MAP = {};
MARKET_NODES.forEach((n) => { NODE_MAP[n.id] = n; });

// ALL L2 nodes are terminal forecasts
const TERMINAL_IDS = MARKET_NODES.filter((n) => n.layer === 2).map((n) => n.id);
const TERMINAL_Y = TERMINAL_IDS.map((id) => NODE_MAP[id].y);

export default function ForecastSimulation() {
  const [running, setRunning] = useState(false);
  const [pubItems, setPubItems] = useState([]);
  const [priItems, setPriItems] = useState([]);
  const [forecasts, setForecasts] = useState(
    () => TERMINAL_IDS.map(() => 10 + Math.random() * 80)
  );
  const [fcFlash, setFcFlash] = useState(() => TERMINAL_IDS.map(() => false));

  const nodeAct = useRef({});
  const edgeAct = useRef({});
  const agentAct = useRef({});
  const agentStep = useRef({});    // agentId → step index
  const agentLine = useRef({});    // "agentId-nodeId" → count
  const agentLineColor = useRef({}); // "agentId-nodeId" → hex color (collab)
  const agentCollab = useRef({});  // agentId → hex color (while collaborating)
  const agentGen = useRef({});     // agentId → generation counter (prevents stale timers)
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((v) => v + 1), []);

  const timersRef = useRef([]);
  const intervalsRef = useRef([]);
  const boxRef = useRef(null);
  const pubIdxRef = useRef(0);
  const priIdxRef = useRef(0);

  const timer = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
  }, []);

  const activate = useCallback((ref, key, dur) => {
    ref.current[key] = (ref.current[key] || 0) + 1;
    bump();
    timer(() => {
      ref.current[key] = Math.max(0, (ref.current[key] || 0) - 1);
      bump();
    }, dur);
  }, [bump, timer]);

  const isLit = (ref, key) => (ref.current[key] || 0) > 0;

  /* Propagation through market graph */
  const propagate = useCallback((startId) => {
    activate(nodeAct, startId, 2600);
    const layers = {};
    const visited = new Set([startId]);
    const queue = [{ id: startId, depth: 0 }];
    while (queue.length > 0) {
      const { id, depth } = queue.shift();
      (CHILDREN[id] || []).forEach((child) => {
        if (!visited.has(child)) {
          visited.add(child);
          if (!layers[depth]) layers[depth] = [];
          layers[depth].push({ from: id, to: child });
          queue.push({ id: child, depth: depth + 1 });
        }
      });
    }

    Object.entries(layers).forEach(([depth, edges]) => {
      const delay = Number(depth) * 450 + 200;
      edges.forEach((e) => {
        timer(() => {
          activate(edgeAct, `${e.from}-${e.to}`, 1600);
          activate(nodeAct, e.to, 2000);

          const tIdx = TERMINAL_IDS.indexOf(e.to);
          if (tIdx !== -1) {
            setForecasts((prev) => {
              const next = [...prev];
              next[tIdx] = Math.max(1, Math.min(99,
                next[tIdx] + (Math.random() - 0.5) * 3));
              return next;
            });
            setFcFlash((prev) => {
              const next = [...prev];
              next[tIdx] = true;
              return next;
            });
            timer(() => {
              setFcFlash((prev) => {
                const next = [...prev];
                next[tIdx] = false;
                return next;
              });
            }, 800);
          }
        }, delay + Math.random() * 120);
      });
    });
  }, [activate, timer]);

  const COLLAB_COLORS = ['#5B8DEF', '#E8A838', '#7EC875', '#C777DA'];
  let collabColorIdx = useRef(0);

  /* Fire single agent.
     opts.color: collab color (null for solo)
     opts.targets: forced target node IDs (null = pick own)
     opts.fireAt: ms delay at which to fire lines into graph (null = natural timing)
     Uses a generation counter so that if the agent is re-fired mid-process,
     all stale timers from the previous run become no-ops. */
  const fireAgentSolo = useCallback((agentIdx, opts = {}) => {
    const { color = null, targets: forcedTargets = null, fireAt = null } = opts;
    const agent = AGENTS[agentIdx];
    const stepCount = agent.steps.length;
    const naturalFire = (stepCount - 1) * 900;
    const fireDelay = fireAt != null ? fireAt : naturalFire;
    const totalDur = Math.max(stepCount * 900, fireDelay) + 600;

    // Increment generation — all timers from previous runs become stale
    const gen = (agentGen.current[agentIdx] || 0) + 1;
    agentGen.current[agentIdx] = gen;
    const isStale = () => agentGen.current[agentIdx] !== gen;

    // Clear any leftover state from previous run
    delete agentCollab.current[agentIdx];
    agentStep.current[agentIdx] = -1;

    activate(agentAct, agentIdx, totalDur);

    // Set collab color on the card
    if (color) {
      agentCollab.current[agentIdx] = color;
      bump();
      timer(() => {
        if (isStale()) return;
        delete agentCollab.current[agentIdx]; bump();
      }, totalDur + 400);
    }

    // Cycle through steps
    agent.steps.forEach((_, si) => {
      timer(() => {
        if (isStale()) return;
        agentStep.current[agentIdx] = si;
        bump();
      }, si * 900);
    });

    // Fire lines into graph at fireDelay
    timer(() => {
      if (isStale()) return;
      const picked = forcedTargets || (() => {
        const count = Math.random() < 0.3 ? 2 : 1;
        const p = [];
        for (let i = 0; i < count; i++) {
          const c = agent.targets[Math.floor(Math.random() * agent.targets.length)];
          if (!p.includes(c)) p.push(c);
        }
        return p;
      })();
      picked.forEach((nId) => {
        const lineKey = `${agentIdx}-${nId}`;
        if (color) agentLineColor.current[lineKey] = color;
        activate(agentLine, lineKey, 1400);
        timer(() => { delete agentLineColor.current[lineKey]; }, 1400);
      });
      timer(() => {
        if (isStale()) return;
        picked.forEach((nId) => propagate(nId));
      }, 350);
    }, fireDelay);

    // Clear step
    timer(() => {
      if (isStale()) return;
      agentStep.current[agentIdx] = -1;
      bump();
    }, totalDur);
  }, [activate, timer, bump, propagate]);

  /* Fire agent — may trigger collaboration.
     Skips if the agent is already mid-process to prevent overlaps. */
  const fireAgent = useCallback((agentIdx) => {
    if (isLit(agentAct, agentIdx)) return; // already busy

    // 30% chance to collaborate
    if (Math.random() < 0.30) {
      // Pick a partner that isn't already busy
      const candidates = AGENTS.filter((_, i) => i !== agentIdx && !isLit(agentAct, i));
      if (candidates.length === 0) {
        fireAgentSolo(agentIdx);
        return;
      }
      const partner = candidates[Math.floor(Math.random() * candidates.length)];
      const color = COLLAB_COLORS[collabColorIdx.current % COLLAB_COLORS.length];
      collabColorIdx.current++;

      // Pick shared target from intersection of targets, or union if no overlap
      const shared = AGENTS[agentIdx].targets.filter((t) => partner.targets.includes(t));
      const targetPool = shared.length > 0 ? shared : [...new Set([...AGENTS[agentIdx].targets, ...partner.targets])];
      const sharedTarget = [targetPool[Math.floor(Math.random() * targetPool.length)]];

      // Both fire lines at the same moment = max of their natural fire times
      const aSteps = AGENTS[agentIdx].steps.length;
      const bSteps = partner.steps.length;
      const syncFire = Math.max((aSteps - 1) * 900, (bSteps - 1) * 900);

      fireAgentSolo(agentIdx, { color, targets: sharedTarget, fireAt: syncFire });
      fireAgentSolo(partner.id, { color, targets: sharedTarget, fireAt: syncFire });
    } else {
      fireAgentSolo(agentIdx);
    }
  }, [fireAgentSolo]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Start simulation */
  const startSim = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timersRef.current = [];
    intervalsRef.current = [];
    nodeAct.current = {};
    edgeAct.current = {};
    agentAct.current = {};
    agentStep.current = {};
    agentLine.current = {};
    agentLineColor.current = {};
    agentCollab.current = {};
    agentGen.current = {};
    setPubItems([]);
    setPriItems([]);
    setForecasts(TERMINAL_IDS.map(() => 10 + Math.random() * 80));
    setFcFlash(TERMINAL_IDS.map(() => false));
    pubIdxRef.current = 0;
    priIdxRef.current = 0;
    setRunning(true);

    // Public stream — fast (every 1.8s), show more items
    timer(() => {
      setPubItems([{ id: Date.now(), text: PUBLIC_POOL[0] }]);
      pubIdxRef.current = 1;
    }, 200);
    intervalsRef.current.push(setInterval(() => {
      const idx = pubIdxRef.current % PUBLIC_POOL.length;
      pubIdxRef.current++;
      setPubItems((prev) => [{ id: Date.now(), text: PUBLIC_POOL[idx] }, ...prev].slice(0, 12));
    }, 1800));

    // Private stream — slower (every 4.5s), fewer items
    timer(() => {
      setPriItems([{ id: Date.now(), text: PRIVATE_POOL[0] }]);
      priIdxRef.current = 1;
    }, 1200);
    intervalsRef.current.push(setInterval(() => {
      const idx = priIdxRef.current % PRIVATE_POOL.length;
      priIdxRef.current++;
      setPriItems((prev) => [{ id: Date.now(), text: PRIVATE_POOL[idx] }, ...prev].slice(0, 6));
    }, 4500));

    // Agent firing — staggered, 12-20s per agent (slower)
    AGENTS.forEach((_, i) => {
      const baseDelay = 3000 + i * 1800;
      const interval = 12000 + Math.floor(Math.random() * 8000) + i * 600;
      timer(() => fireAgent(i), baseDelay);
      intervalsRef.current.push(setInterval(() => fireAgent(i), interval));
    });
  }, [timer, fireAgent]);

  const stopSim = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timersRef.current = [];
    intervalsRef.current = [];
    setRunning(false);
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) startSim(); else stopSim(); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); stopSim(); };
  }, [startSim, stopSim]);

  const getStep = (id) => agentStep.current[id] ?? -1;

  // Agent center Y for SVG lines (matches card center)

  return (
    <div className="sim" ref={boxRef}>
      {/* ── INPUTS ── */}
      <div className="sim__inputs">
        <div className="sim__input-group">
          <div className="sim__input-header">
            <span className="sim__input-dot sim__input-dot--pub" />
            <span className="sim__input-label">Public Information</span>
          </div>
          <div className="sim__stream">
            {pubItems.map((item, i) => (
              <div key={item.id} className={`sim__si${i >= 5 ? ' sim__si--old' : ''}`}>
                {item.text}
              </div>
            ))}
          </div>
        </div>
        <div className="sim__input-group">
          <div className="sim__input-header">
            <span className="sim__input-dot sim__input-dot--pri" />
            <span className="sim__input-label">Private Information</span>
          </div>
          <div className="sim__stream">
            {priItems.map((item, i) => (
              <div key={item.id} className={`sim__si sim__si--pri${i >= 3 ? ' sim__si--old' : ''}`}>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── IN-CONNECTOR ── */}
      <div className="sim__conn">
        <svg viewBox="0 0 20 100" preserveAspectRatio="none">
          <line x1="0" y1="30" x2="20" y2="30" className="sim__cline sim__cline--pub" />
          <line x1="0" y1="70" x2="20" y2="70" className="sim__cline sim__cline--pri" />
        </svg>
      </div>

      {/* ── ENGINE ── */}
      <div className="sim__engine">
        <div className="sim__engine-inner">
          {/* Zone labels */}
          <span className="sim__elabel sim__elabel--agents">Agent Swarm</span>
          <span className="sim__elabel sim__elabel--markets">Belief Graph</span>

          {/* SVG layer */}
          <svg className="sim__engine-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Vertical divider */}
            <line x1="36" y1="1" x2="36" y2="99" className="sim__divider-line" />

            {/* Agent → L0 lines (colored when collaborating) */}
            {AGENTS.map((agent) =>
              agent.targets.map((nId) => {
                const target = NODE_MAP[nId];
                const lineKey = `${agent.id}-${nId}`;
                const lit = isLit(agentLine, lineKey);
                const color = agentLineColor.current[lineKey];
                return (
                  <line key={`a${agent.id}-n${nId}`}
                    x1="33" y1={agent.cy + 7}
                    x2={target.x} y2={target.y}
                    className={`sim__aline${lit ? ' sim__aline--on' : ''}`}
                    style={lit && color ? { stroke: color, filter: `drop-shadow(0 0 3px ${color})` } : undefined}
                  />
                );
              })
            )}

            {/* Market edges */}
            {MARKET_EDGES.map(([from, to], i) => (
              <line key={`e${i}`}
                x1={NODE_MAP[from].x} y1={NODE_MAP[from].y}
                x2={NODE_MAP[to].x} y2={NODE_MAP[to].y}
                className={`sim__gedge${isLit(edgeAct, `${from}-${to}`) ? ' sim__gedge--on' : ''}`}
              />
            ))}

            {/* Output flow lines from ALL terminal nodes → right edge */}
            {TERMINAL_IDS.map((nId, i) => (
              <line key={`of${i}`}
                x1={NODE_MAP[nId].x} y1={NODE_MAP[nId].y}
                x2="100" y2={NODE_MAP[nId].y}
                className={`sim__outflow${isLit(nodeAct, nId) ? ' sim__outflow--on' : ''}`}
              />
            ))}
          </svg>

          {/* Agent cards — vertical step list, collab color */}
          {AGENTS.map((a) => {
            const active = isLit(agentAct, a.id);
            const step = getStep(a.id);
            const cColor = agentCollab.current[a.id];
            const cardStyle = { top: `${a.cy}%` };
            if (cColor) {
              cardStyle.borderColor = cColor;
              cardStyle.boxShadow = `0 0 16px ${cColor}33, inset 0 0 12px ${cColor}0a`;
            }
            return (
              <div key={`ac${a.id}`}
                className={`sim__acard${active ? ' sim__acard--active' : ''}${cColor ? ' sim__acard--collab' : ''}`}
                style={cardStyle}
              >
                <div className="sim__acard-head">
                  <span className={`sim__acard-type${a.sme ? ' sim__acard-type--sme' : ''}`}>
                    {a.sme ? 'SME' : 'GEN'}
                  </span>
                  <span className="sim__acard-name">{a.name}</span>
                  {cColor && <span className="sim__acard-collab-dot" style={{ background: cColor }} />}
                </div>
                <div className="sim__acard-steps">
                  {a.steps.map((s, si) => (
                    <div key={si} className={`sim__acard-row${active && si === step ? ' sim__acard-row--active' : ''}${active && si < step ? ' sim__acard-row--done' : ''}`}
                      style={active && si === step && cColor ? { borderLeftColor: cColor, background: `${cColor}18` } : undefined}
                    >
                      <span className="sim__acard-row-num">{si + 1}</span>
                      <span className="sim__acard-row-action">{s.action}</span>
                      <span className="sim__acard-row-tool">{s.tool}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Market nodes */}
          {MARKET_NODES.map((n) => (
            <div key={`n${n.id}`}
              className={`sim__gnode${isLit(nodeAct, n.id) ? ' sim__gnode--on' : ''} sim__gnode--l${n.layer}`}
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            />
          ))}
        </div>
      </div>

      {/* ── OUT-CONNECTOR ── */}
      <div className="sim__conn">
        <svg viewBox="0 0 20 100" preserveAspectRatio="none">
          {TERMINAL_Y.map((y, i) => (
            <line key={i} x1="0" y1={y} x2="20" y2={y}
              className={`sim__cline${isLit(nodeAct, TERMINAL_IDS[i]) ? ' sim__cline--active' : ''}`}
            />
          ))}
        </svg>
      </div>

      {/* ── FORECASTS ── */}
      <div className="sim__outputs">
        <div className="sim__fc-list">
          {forecasts.map((val, i) => (
            <div key={i}
              className={`sim__fc${running ? ' sim__fc--on' : ''}${fcFlash[i] ? ' sim__fc--flash' : ''}`}
              style={{ top: `${TERMINAL_Y[i]}%` }}
            >
              <span className="sim__fc-val">{val.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
